import { useGetReservationsQuery, useToday } from "@stepapp/core";
import { ReservationStatus } from "@stepapp/core/apiTypes";
import { format, subDays } from "date-fns";
import { useFocusEffect } from "expo-router";
import { useCallback, useMemo } from "react";
import { AppStorage } from "../services/AppStorage";

const ReservationCompletedStatuses = [
	ReservationStatus.Accepted,
	ReservationStatus.AwaitingPayment,
	ReservationStatus.Completed,
	ReservationStatus.PaymentReceived,
	ReservationStatus.Refunded,
	ReservationStatus.CustomerReview,
];

export const useFindServicesForReview = () => {
	const isAuthenticated = !!AppStorage().get("token");
	const today = useToday();
	const startDay = subDays(today, 6);

	const { data, refetch: refetchServicesToReview } = useGetReservationsQuery({
		skip: !isAuthenticated,
		fetchPolicy: "cache-and-network",
		variables: {
			filter: {
				status: ReservationCompletedStatuses,
				startDate: format(startDay, "yyyy-MM-dd"),
				endDate: format(today, "yyyy-MM-dd"),
			},
			isCustomerApp: true,
		},
	});

	const {
		data: retryPaymentServices,
		refetch: refetchServicesWithPaymentFailed,
	} = useGetReservationsQuery({
		fetchPolicy: "cache-and-network",
		skip: !isAuthenticated,
		variables: {
			filter: { status: [ReservationStatus.PaymentFailed] },
			isCustomerApp: true,
		},
	});

	const servicesForReview = useMemo(
		() =>
			data?.listReservations?.filter((order) => {
				return (
					[ReservationStatus.CustomerReview].includes(order?.status) ||
					(ReservationCompletedStatuses.includes(order?.status) &&
						!order?.review?.id)
				);
			}),
		[data?.listReservations],
	);

	useFocusEffect(
		// biome-ignore lint/correctness/useExhaustiveDependencies: reason
		useCallback(() => {
			if (isAuthenticated) {
				refetchServicesToReview();
				refetchServicesWithPaymentFailed();
			}
		}, [refetchServicesToReview, refetchServicesWithPaymentFailed]),
	);

	const refetchServices = useCallback(() => {
		refetchServicesWithPaymentFailed();
		refetchServicesToReview();
	}, [refetchServicesWithPaymentFailed, refetchServicesToReview]);

	return useMemo(
		() => ({
			servicesForReview,
			servicesToRetryPayment: retryPaymentServices?.listReservations,
			refetch: refetchServices,
		}),
		[
			refetchServices,
			retryPaymentServices?.listReservations,
			servicesForReview,
		],
	);
};
